import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { formValues, schema } from "./validations";
import { zodResolver } from "@hookform/resolvers/zod";
import { ADDRESS, ALTERNATE_LOGIN_ID, EMAIL_ID_1, FIRST_NAME, LAST_NAME, MOBILE_NO, USER_ID,
  ADDRESS_1,
  ADDRESS_2,
  CITY,
  EMAIL_ID_2,
  END_DATE,
  LANDLINE_NO,
  OCCUPANCY_TYPE,
  PO_BOX,
  START_DATE,
  STATE,
  USERNAME,
  ZIPCODE,
  USER_TYPE,
  OWNERSHIP_SHARE,
  EXISTING,
  ROW_INDEX,
  INDIVIDUAL,
 } from "../consts";
import { UserType } from "../types";
import { getRequiredFields, ROLE_USER } from "@/lib/utils";
import { useGetState, useGetUserById, useGetUsersList, useValidateUserData } from "@/services";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { toast } from "@/components/Toast";

const useAddEditDialog = ({ onSave, data, isEdit, ownershipType }:
  { onSave: (data: any) => void; data?: any; isEdit?: boolean, ownershipType: string }) => {
  const [pageNumber, setPageNumber] = useState(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const isDisableShare = ownershipType == INDIVIDUAL;
  const propertyData = useSelector((state: RootState) => state.currentData.propertyData);

  const form = useForm<formValues>({
    defaultValues: {
      [ROW_INDEX]: "",
      [USER_TYPE]: EXISTING,
      [USER_ID]: "",
      [OWNERSHIP_SHARE]: isDisableShare ? "100" : "",
      [ALTERNATE_LOGIN_ID]: "",
      [FIRST_NAME]: "",
      [LAST_NAME]: "",
      [MOBILE_NO]: "",
      [EMAIL_ID_1]: "",
      [ADDRESS]: {
        [ADDRESS_1]: "",
        [ADDRESS_2]: "",
        [ZIPCODE]: "",
        [STATE]: "",
        [CITY]: "",
        [PO_BOX]: "",
      },
      [OCCUPANCY_TYPE]: "",
      [LANDLINE_NO]: "",
      [EMAIL_ID_2]: "",
      [START_DATE]: "",
      [END_DATE]: "",
      [USERNAME]: "",
    },
    resolver: zodResolver(schema),
  });
  
  const requiredFields = getRequiredFields(schema);
  const formData = useWatch({ control: form.control });

  const [keys, setKeys] = useState<any>({
    state: Math.random() * 2,
    city: Math.random() * 2,
    type: Math.random() * 2
  });
  
  const { data: stateList, isLoading: isStateLoading } = useGetState({});
  
  const {
    data: userList,
    isLoading: isUserListLoading,
    refetch: refetchUser,
    isRefetching: isRefetchingUser,
  } = useGetUsersList({
    data: {
      filters: [
        {
          field: "role",
          value: ROLE_USER,
          operator: "CONTAINS",
        },
      ],
      search: searchTerm,
      searchFields: ["name"],
      pageNumber,
      pageSize: 10
    },
  });

  const isUsrListLoading = isUserListLoading || isRefetchingUser;

  useEffect(() => {
    if (isEdit && data) {
      form.reset({...data});
    }
  }, [isEdit, data]);

  useEffect(() => {
    if (userList?.data) {
      const incomingOptions = userList.data.map((d: any) => ({
        label: `${d?.firstName || ""} ${d?.lastName || ""}`.trim() || d?.username,
        value: d?.userId?.toString()
      }));

      // Check if we reached the last page
      setHasMore(!userList?.last);

      if (searchTerm) {
        setUserOptions(incomingOptions);
      } else {
        setUserOptions((prevOptions) => {
          // Reset list on first page
          if (pageNumber === 0) return incomingOptions;
  
          // Deduplicate using a Map/Set on unique ID values
          const existingIds = new Set(prevOptions.map((opt) => opt.value));
          const filteredNew = incomingOptions.filter((opt: any) => !existingIds.has(opt.value));
          
          return [...prevOptions, ...filteredNew];
        });
      }
    }
  }, [userList, pageNumber]);

  const onUserTypeChange = (value: UserType) => {
    form.setValue(USER_TYPE, value)
  }

  const { data: validateUserData, isRefetching: isValidateUserDataLoading, refetch: refetchValidateUserData } = useValidateUserData({
    params: { 
      userName: formData[USERNAME],
      email: formData[EMAIL_ID_1],
      email2: formData[EMAIL_ID_2],
      phoneNumber: formData[MOBILE_NO],
      loginId: formData[ALTERNATE_LOGIN_ID],
    },
    queryParams: {
      enabled: false,
    },
  });

  useEffect(() => {
    if (validateUserData?.data) {
      const data = validateUserData?.data?.checks || [];
      const isEmail1Exists = data?.find((data: any) => data?.field === "email" && data?.available);
      const isEmail2Exists = data?.find((data: any) => data?.field === "email2" && data?.available);
      const isPhoneNumberExists = data?.find((data: any) => data?.field === "phoneNumber" && data?.available);
      const isLoginIdExists = data?.find((data: any) => data?.field === "loginId" && data?.available);
      const isUsernameExists = data?.find((data: any) => data?.field === "userName" && data?.available);

      let msg = ""
      if (!isEmail1Exists) {
        msg += "Email 1 already exists";
      }
      if (!isEmail2Exists) {
        msg += "Email 2 already exists";
      }
      if (!isPhoneNumberExists) {
        msg += "Phone number already exists";
      }
      if (!isLoginIdExists) {
        msg += "Login ID already exists";
      }
      if (!isUsernameExists) {
        msg += "Username already exists";
      }

      if (msg) {
        toast.error(msg);
      } else {  
        onSave(formData);
      }
    }
  }, [validateUserData]);

  const onSaveData = () => {
    if (formData[USER_TYPE] == EXISTING) {
      onSave(formData);
    } else {  
      refetchValidateUserData();
    }
  }

  const loadMoreOptions = () => {
    if (!isUsrListLoading && hasMore) {
      setPageNumber((prev) => prev + 1);
      setTimeout(() => refetchUser(), 100);
    }
  };

  useEffect(() => {
    if (!isUsrListLoading) {
      if (searchTerm?.length > 2 || !searchTerm) {
        setPageNumber(0);
        setTimeout(() => refetchUser(), 100);
      }
    }
  }, [searchTerm]);
  
  const {
    data: userDetails,
    isLoading: isUserDetailsLoading,
  } = useGetUserById({
    id: formData[USER_ID] as string,
    queryParams: {
      enabled: !!formData[USER_ID],
    },
  });
  
  useEffect(() => {
    if (userDetails) {
      const userData = userDetails?.data;
      form.reset({
        ...formData,
        [USER_ID]: userData?.[USER_ID]?.toString() || "",
        [ALTERNATE_LOGIN_ID]: userData?.[ALTERNATE_LOGIN_ID]?.toString() || "",
        [FIRST_NAME]: userData?.[FIRST_NAME]?.toString() || "",
        [LAST_NAME]: userData?.[LAST_NAME]?.toString() || "",
        [MOBILE_NO]: userData?.[MOBILE_NO]?.toString() || "",
        [LANDLINE_NO]: userData?.[LANDLINE_NO]?.toString() || undefined,
        [EMAIL_ID_1]: userData?.[EMAIL_ID_1]?.toString() || "",
        [EMAIL_ID_2]: userData?.[EMAIL_ID_2]?.toString() || undefined,
        [ADDRESS]: {
          [ADDRESS_1]: userData?.[ADDRESS]?.[ADDRESS_1]?.toString() || "",
          [ADDRESS_2]: userData?.[ADDRESS]?.[ADDRESS_2]?.toString() || undefined,
          [ZIPCODE]: userData?.[ADDRESS]?.[ZIPCODE]?.toString() || "",
          [STATE]:
            stateList?.data?.find(
              (state: { name: string; code: string }) =>
                state.name == userData?.[ADDRESS]?.[STATE] ||
                state.code == userData?.[ADDRESS]?.[STATE]
            )?.code || "",
          [CITY]: userData?.[ADDRESS]?.[CITY]?.toString() || "",
          [PO_BOX]: userData?.[ADDRESS]?.[PO_BOX]?.toString() || "",
        },
        [OCCUPANCY_TYPE]: userData?.propertyOccupancies?.find((data: any) => data?.propertyId?.toString() == propertyData?.propertyId?.toString())?.occupancyType?.toString() || "",
        // [START_DATE]: userData?.[START_DATE] || "",
        // [END_DATE]: userData?.[END_DATE] || "",
        [USERNAME]: userData?.[USERNAME]?.toString() || "",
      });
      setKeys((prev: any) => ({
        ...prev,
        state: Math.random() * 2,
        city: Math.random() * 2,
        type: Math.random() * 2,
      }));
    }
  }, [userDetails]);

  // const onSearchChange = (value: any) => {
  //   setSearchTerm(value);
  //   setPageNumber(0);
  //   setTimeout(() => {
  //     refetchUser();
  //   }, 100)
  // }

  const columns = [
    {
      accessorKey: "communityName",
      header: <div>Community</div>,
      accessorFn: (row: any) => String(row?.communityName || ""),
    },
    {
      accessorKey: "propertyDisplayId",
      header: <div>Property ID</div>,
      accessorFn: (row: any) => String(row?.propertyDisplayId || ""),
    },
    {
      accessorKey: "propertyName",
      header: <div>Property Name</div>,
      accessorFn: (row: any) => String(row?.propertyName || ""),
    },
  ];

  const associatedCommunities = useMemo(() => userDetails?.data?.propertyOccupancies?.filter((data: any) => data?.propertyId?.toString() != propertyData?.propertyId?.toString()), [userDetails])

  const onUpdateUserType: any = (type: "new" | "existing", userId?: string) => {
    form.setValue(USER_TYPE, type);

    if (type == "existing") form.setValue(USER_ID, userId)
  }

  return {
    keys,
    onUserTypeChange,
    form,
    requiredFields,
    formData,
    userOptions,
    isUsrListLoading,
    stateList: stateList?.data || [],
    onSaveData,
    loadMoreOptions,
    hasMore,
    isLoading: isStateLoading,
    isUserDetailsLoading,
    isExisting: (formData as any)[USER_TYPE] == EXISTING,
    isDisableShare,
    onSearchChange: setSearchTerm,
    associatedCommunities,
    columns,
    setSearchTerm,
    searchTerm,
    userList,
    onUpdateUserType,
    isValidateUserDataLoading
  }
}

export default useAddEditDialog;